import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { useConfig } from '@nusrif/contexts';
import { appUpdateService, AppUpdateInfo } from '@nusrif/services';

interface UseAppUpdateReturn {
  showUpdateModal: boolean;
  setShowUpdateModal: (show: boolean) => void;
  updateInfo: AppUpdateInfo | null;
  checkForUpdates: () => Promise<void>;
  handleUpdate: () => Promise<void>;
  handleCloseModal: () => void;
  isLoading: boolean;
}

export const useAppUpdate = (): UseAppUpdateReturn => {
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<AppUpdateInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const config = useConfig();

  const checkForUpdates = useCallback(async () => {
    if (!config) return;
    // Only run update checks on native platforms
    if (!Capacitor.isNativePlatform()) return;

    try {
      setIsLoading(true);
      const info = await appUpdateService.checkForUpdates();
      setUpdateInfo(info);

      // Show modal if there's an update and it's either forced or we haven't shown it recently
      if (info.hasUpdate) {
        const shouldShowModal = () => {
          // Always show for force updates
          if (info.isForceUpdate || info.forceUpdate) {
            return true;
          }

          // For regular updates, check if we've shown it recently (within 24 hours)
          const lastShown = localStorage.getItem('appUpdateModalLastShown');
          if (lastShown) {
            const lastShownDate = new Date(lastShown);
            const twentyFourHoursAgo = new Date();
            twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
            
            if (lastShownDate > twentyFourHoursAgo) {
              return false;
            }
          }
          
          return true;
        };

        if (shouldShowModal()) {
          setShowUpdateModal(true);
          localStorage.setItem('appUpdateModalLastShown', new Date().toISOString());
        }
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
    } finally {
      setIsLoading(false);
    }
  }, [config]);

  const handleUpdate = useCallback(async () => {
    try {
      await appUpdateService.openStore();
      // Close modal after opening store only if it's not a force update
      if (updateInfo && !updateInfo.isForceUpdate && !updateInfo.forceUpdate) {
        setShowUpdateModal(false);
      }
    } catch (error) {
      console.error('Failed to open store:', error);
    }
  }, [updateInfo]);

  const handleCloseModal = useCallback(() => {
    // Only allow closing if it's not a force update
    if (updateInfo && !updateInfo.isForceUpdate && !updateInfo.forceUpdate) {
      setShowUpdateModal(false);
    }
  }, [updateInfo]);

  // Check for updates on mount (only native due to early return in checkForUpdates)
  useEffect(() => {
    if (config) {
      const timer = setTimeout(() => {
        checkForUpdates();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [checkForUpdates, config]);

  // Check for updates when config changes (only native due to early return)
  useEffect(() => {
    if (config) {
      checkForUpdates();
    }
  }, [config, checkForUpdates]);

  return {
    showUpdateModal,
    setShowUpdateModal,
    updateInfo,
    checkForUpdates,
    handleUpdate,
    handleCloseModal,
    isLoading,
  };
}; 